import { ref, computed, onMounted } from 'vue'
import { auth, db } from '@/firebase/firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  deleteUser,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore'

// 전역 상태
const user = ref(null)
const userProfile = ref(null)
const loading = ref(true)
const error = ref(null)

// 초기화 여부
let initialized = false

// Google Provider
const googleProvider = new GoogleAuthProvider()

export function useAuth() {
  // 계산된 속성
  const isLoggedIn = computed(() => !!user.value)
  const isPartner = computed(() => userProfile.value?.role === 'partner')
  const isAdmin = computed(() => userProfile.value?.role === 'admin')

  // Auth 상태 초기화
  const initAuth = () => {
    if (initialized) return

    onAuthStateChanged(auth, async (firebaseUser) => {
      user.value = firebaseUser
      if (firebaseUser) {
        await fetchUserProfile(firebaseUser.uid)
      } else {
        userProfile.value = null
      }
      loading.value = false
    })

    initialized = true
  }

  // 사용자 프로필 가져오기
  const fetchUserProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        userProfile.value = docSnap.data()
      } else {
        userProfile.value = null
      }
    } catch (e) {
      console.error('프로필 로드 실패:', e)
      userProfile.value = null
    }
  }

  // 사용자 프로필 생성 (신규 가입 시)
  const createUserProfile = async (firebaseUser) => {
    const profileData = {
      email: firebaseUser.email,
      name: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || '',
      role: 'user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    await setDoc(doc(db, 'users', firebaseUser.uid), profileData)
    userProfile.value = profileData
  }

  // 회원가입 (이메일/비밀번호)
  const register = async (email, password) => {
    error.value = null
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      await createUserProfile(userCredential.user)
      return { success: true }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      return { success: false, error: error.value }
    }
  }

  // 로그인 (이메일/비밀번호)
  const login = async (email, password) => {
    error.value = null
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      // 마지막 로그인 시간 업데이트
      await updateDoc(doc(db, 'users', result.user.uid), {
        lastLoginAt: new Date().toISOString()
      })
      return { success: true }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      return { success: false, error: error.value }
    }
  }

  // 구글 로그인
  const loginWithGoogle = async () => {
    error.value = null
    try {
      const result = await signInWithPopup(auth, googleProvider)

      // 기존 프로필 확인
      const docSnap = await getDoc(doc(db, 'users', result.user.uid))
      if (!docSnap.exists()) {
        // 신규 사용자면 프로필 생성
        await createUserProfile(result.user)
      } else {
        // 기존 사용자면 마지막 로그인 시간 업데이트
        await updateDoc(doc(db, 'users', result.user.uid), {
          lastLoginAt: new Date().toISOString()
        })
      }

      return { success: true }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      return { success: false, error: error.value }
    }
  }

  // 로그아웃
  const logout = async () => {
    try {
      await signOut(auth)
      userProfile.value = null
      return { success: true }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      return { success: false, error: error.value }
    }
  }

  // 프로필 업데이트
  const updateUserProfile = async (profileData) => {
    error.value = null
    try {
      await updateDoc(doc(db, 'users', user.value.uid), {
        ...profileData,
        updatedAt: new Date().toISOString()
      })

      await fetchUserProfile(user.value.uid)
      return { success: true }
    } catch (e) {
      error.value = '프로필 업데이트에 실패했습니다.'
      return { success: false, error: error.value }
    }
  }

  // 역할 변경 (user -> partner)
  const upgradeToPartner = async () => {
    return await updateUserProfile({ role: 'partner' })
  }

  // 비밀번호 변경
  const changePassword = async (currentPassword, newPassword) => {
    error.value = null
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        return { success: false, error: '로그인이 필요합니다.' }
      }

      // 현재 비밀번호로 재인증
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
      await reauthenticateWithCredential(currentUser, credential)

      // 비밀번호 변경
      await updatePassword(currentUser, newPassword)

      return { success: true }
    } catch (e) {
      console.error('비밀번호 변경 실패:', e)
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        return { success: false, error: '현재 비밀번호가 올바르지 않습니다.' }
      }
      if (e.code === 'auth/weak-password') {
        return { success: false, error: '새 비밀번호는 6자 이상이어야 합니다.' }
      }
      return { success: false, error: '비밀번호 변경에 실패했습니다.' }
    }
  }

  // 알림 설정 업데이트
  const updateNotificationSettings = async (settings) => {
    return await updateUserProfile({ notificationSettings: settings })
  }

  // 회원 탈퇴
  const deleteAccount = async () => {
    error.value = null
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        return { success: false, error: '로그인이 필요합니다.' }
      }

      // Firestore에서 사용자 프로필 삭제
      await deleteDoc(doc(db, 'users', currentUser.uid))

      // Firebase Auth에서 사용자 삭제
      await deleteUser(currentUser)

      // 상태 초기화
      user.value = null
      userProfile.value = null

      return { success: true }
    } catch (e) {
      console.error('회원 탈퇴 실패:', e)
      // 최근 로그인이 필요한 경우
      if (e.code === 'auth/requires-recent-login') {
        return { success: false, error: '보안을 위해 다시 로그인 후 탈퇴해주세요.' }
      }
      error.value = '회원 탈퇴에 실패했습니다.'
      return { success: false, error: error.value }
    }
  }

  // 에러 메시지 변환
  const getErrorMessage = (code) => {
    const messages = {
      'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
      'auth/invalid-email': '유효하지 않은 이메일 형식입니다.',
      'auth/operation-not-allowed': '해당 인증 방식이 비활성화되어 있습니다.',
      'auth/weak-password': '비밀번호는 6자 이상이어야 합니다.',
      'auth/user-disabled': '비활성화된 계정입니다.',
      'auth/user-not-found': '존재하지 않는 계정입니다.',
      'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
      'auth/invalid-credential': '이메일 또는 비밀번호가 올바르지 않습니다.',
      'auth/too-many-requests': '너무 많은 요청이 있었습니다. 잠시 후 다시 시도해주세요.',
      'auth/popup-closed-by-user': '로그인 창이 닫혔습니다. 다시 시도해주세요.',
      'auth/cancelled-popup-request': '로그인이 취소되었습니다.',
      'auth/account-exists-with-different-credential': '이미 다른 방식으로 가입된 이메일입니다.'
    }
    return messages[code] || `오류가 발생했습니다. (${code})`
  }

  // 컴포넌트 마운트 시 초기화
  onMounted(() => {
    initAuth()
  })

  return {
    // 상태
    user,
    userProfile,
    loading,
    error,

    // 계산된 속성
    isLoggedIn,
    isPartner,
    isAdmin,

    // 메서드
    initAuth,
    register,
    login,
    loginWithGoogle,
    logout,
    updateUserProfile,
    upgradeToPartner,
    fetchUserProfile,
    deleteAccount,
    changePassword,
    updateNotificationSettings
  }
}
